/**
 * Inquiry-type taxonomy document schema, defaults, and paths.
 *
 * The classifier's inquiry-type vocabulary lives as data under the reserved
 * `_agent` bucket (`reports/_agent/inquiryTypes/{typeId}`) so admins can add,
 * rename, retune, or deprecate a type without a code change or a functions
 * redeploy. The classifier reads the active set at the top of every run, so a
 * newly added type starts appearing on inbound email immediately and a
 * deprecated one stops being assigned while old reports still render.
 *
 * Contract (enforced by the dashboard UI and the reserved-id invariants here,
 * not by Firestore rules):
 * - **Immutable slug.** The document id IS the slug and the value historical
 *   `actionType` strings resolve against, so it is never renamed. Renaming
 *   means deprecating the old type and adding a new one.
 * - **Deprecate-only.** Types are never deleted — `active: false` retires a
 *   type (excluded from the classifier, still renders on old reports). This
 *   keeps every historical `actionType` string resolvable forever.
 * - **Reserved built-ins.** The ids in {@link RESERVED_INQUIRY_TYPE_IDS}
 *   (`spam`/`other`) are seeded `builtIn: true`; they are the classifier's
 *   normalization fallback and must always be present and active, so they
 *   cannot be deprecated. Their description/priority/risk remain editable —
 *   nothing in the pipeline branches on those slugs.
 *
 * Base schema uses {@link FirestoreTimestampSchema} so the admin/ and client/
 * SDK modules can extend it with their own timestamp transforms.
 */
import {z} from 'zod';
import {FirestoreTimestampSchema} from '../../types';
import type {UniqueStringId} from '../../../types/branded';
import {
  AGENT_BUCKET_ID,
  AGENT_INQUIRY_TYPES_SUBCOLLECTION,
  getBucketDocPath,
} from '../reportBucket';
import {ReportPrioritySchema} from '../report';
import {ActionRiskSchema} from './agentConfig';

// ============================================================================
// Branded id
// ============================================================================

/**
 * Firestore document id of an inquiry type
 * (`reports/_agent/inquiryTypes/{typeId}`). The id IS the slug — the value the
 * classifier returns as `category` and reports freeze as `actionType`. It is
 * immutable by contract (see module doc).
 */
export type InquiryTypeId = UniqueStringId<'InquiryTypeId'>;

// ============================================================================
// Reserved ids
// ============================================================================

/**
 * Slugs of the reserved built-in types. `spam` and `other` are the classifier's
 * normalization fallback (any category outside the active set becomes `other`),
 * so they must always exist and stay active — the dashboard blocks deprecating
 * them and blocks editing their slug, but allows editing everything else.
 */
export const RESERVED_INQUIRY_TYPE_IDS = ['spam', 'other'] as const;

/** Inferred union of the reserved built-in slugs. */
export type ReservedInquiryTypeId = (typeof RESERVED_INQUIRY_TYPE_IDS)[number];

/**
 * Whether a slug is a reserved built-in ({@link RESERVED_INQUIRY_TYPE_IDS}).
 * Reserved slugs cannot be deprecated and their ids cannot change.
 *
 * @param id - The inquiry-type slug to test.
 * @returns `true` when `id` is a reserved built-in slug.
 * @throws Never — pure string comparison.
 */
export function isReservedInquiryTypeId(
  id: string,
): id is ReservedInquiryTypeId {
  return RESERVED_INQUIRY_TYPE_IDS.some(reserved => reserved === id);
}

// ============================================================================
// Document
// ============================================================================

/**
 * Base schema for an inquiry type at `reports/_agent/inquiryTypes/{typeId}`.
 *
 * The document id (not stored as a field) is the immutable slug. `description`
 * is fed verbatim to the classifier prompt, so edits change classifier behavior
 * on the next run.
 */
export const InquiryTypeDocumentSchema = z
  .object({
    /** Display label shown in the dashboard and (denormalized) on reports. */
    name: z.string(),
    /**
     * One-line guidance fed verbatim to the classifier prompt so the model can
     * pick this type. Editable at runtime; changes take effect on the next run.
     */
    description: z.string(),
    /**
     * Priority tier (P0–P3) reports of this type are created with. Drives
     * `derivePriority` when the assigned type is known in the taxonomy.
     */
    typicalPriority: ReportPrioritySchema,
    /**
     * Risk classification for this type. A hard input to the auto-reply gate
     * ({@link decideAutoReply}): only `low`-risk types can ever auto-reply,
     * regardless of the per-type opt-in below.
     */
    risk: ActionRiskSchema,
    /**
     * Per-type opt-in for the automatic acknowledgement. When `true` (and
     * {@link InquiryTypeDocument.autoReplyBody} is non-empty), a confidently
     * classified first message of this type is auto-replied to by the agent
     * (subject to the global gate config and {@link decideAutoReply}). Absent is
     * treated as `false`, so seeding a type never silently starts replying.
     */
    autoReplyEnabled: z.boolean().optional(),
    /**
     * Canned plain-text acknowledgement sent when auto-reply fires for this
     * type — typically pointing the sender at self-serve resources. Sent
     * verbatim (no substitution), so its length is capped at the write path
     * (Firestore rules and the admin editor) against
     * {@link AUTO_REPLY_BODY_MAX_LENGTH} rather than here: this schema
     * validates on READ, so a `.max()` would make an over-long document
     * unreadable and silently drop the whole type from the taxonomy.
     * `null`/absent means no body authored, which
     * disables auto-reply for the type even when `autoReplyEnabled` is `true`.
     */
    autoReplyBody: z.string().nullable().optional(),
    /**
     * Whether the type is active. `false` = deprecated: excluded from the
     * classifier's advertised set but still resolvable so old reports render.
     */
    active: z.boolean(),
    /**
     * Whether this is a reserved built-in ({@link RESERVED_INQUIRY_TYPE_IDS}).
     * Built-ins cannot be deprecated or have their slug changed.
     */
    builtIn: z.boolean(),
    /** Server time the type was created. */
    createdAt: FirestoreTimestampSchema,
    /** Server time the type was last edited. */
    updatedAt: FirestoreTimestampSchema,
  })
  .loose();

/** Inferred type for {@link InquiryTypeDocumentSchema}. */
export type InquiryTypeDocument = z.infer<typeof InquiryTypeDocumentSchema>;

/**
 * Maximum stored length, in characters, of
 * {@link InquiryTypeDocument.autoReplyBody}. An acknowledgement is a short
 * paragraph pointing the sender at self-serve resources, so this is generous
 * rather than tight; it exists to stop an accidental paste (a whole thread, a
 * pasted document) from being mailed verbatim to a student.
 *
 * Enforced on the WRITE path only — the Firestore rules for
 * `reports/_agent/inquiryTypes/{typeId}` and the admin editor. The rules file
 * cannot import this constant, so the number is repeated there with a pointer
 * back to this symbol; change both together.
 */
export const AUTO_REPLY_BODY_MAX_LENGTH = 2000;

// ============================================================================
// Defaults (seed + runtime fallback)
// ============================================================================

/**
 * The seed content fields: {@link InquiryTypeDocumentSchema} with the
 * server-set timestamps omitted. Kept as a derivation of the document schema
 * (rather than hand-redeclared) so field types — notably `autoReplyBody`'s
 * `null`/optional shape — can never drift from the stored document. Exported so
 * a seed-write path can validate a payload before stamping timestamps; it is
 * also the source of the {@link InquiryTypeSeed} type below.
 *
 * Strict where the document schema is loose: a stored document may carry keys
 * this schema does not know (that is what `.loose()` buys on read), but a seed
 * is authored in this repo, and `z.infer` of a loose object resolves to an
 * index-signature type that silently accepts a misspelled key — which would
 * leave the intended field `undefined` with no compile error.
 */
export const InquiryTypeSeedFieldsSchema = InquiryTypeDocumentSchema.omit({
  createdAt: true,
  updatedAt: true,
}).strict();

/**
 * A seed inquiry type: the content fields plus the slug, without the
 * server-set timestamps. {@link DEFAULT_INQUIRY_TYPES} entries have this shape
 * so they can serve both as the classifier's runtime fallback (timestamps are
 * irrelevant there) and as the payload the dashboard writes when seeding an
 * empty collection (it adds `createdAt`/`updatedAt` at write time).
 *
 * Derived from {@link InquiryTypeDocumentSchema} (timestamps omitted) and
 * intersected with the `id` slug, so its field types stay identical to the
 * stored document. The slug is the Firestore document id — not a stored field —
 * hence the explicit intersection.
 */
export type InquiryTypeSeed = z.infer<typeof InquiryTypeSeedFieldsSchema> & {
  /** The immutable slug / document id. */
  id: string;
};

/**
 * The seed taxonomy — the nine categories the classifier vocabulary started as,
 * with their descriptions (moved here from the functions classifier so the
 * vocabulary is data, not code). Used in two ways:
 * - **Runtime fallback:** when the `inquiryTypes` collection is empty (e.g. a
 *   fresh project that has not seeded yet), `processEmail` classifies against
 *   this list so ingestion never depends on a seed step.
 * - **Seed payload:** the dashboard's "Seed defaults" writes these documents
 *   (stamping timestamps) into an empty collection.
 *
 * `spam`/`other` are the reserved built-ins (see {@link RESERVED_INQUIRY_TYPE_IDS});
 * the rest are ordinary types an admin may deprecate. Priorities/risks are
 * sensible starting points and are fully admin-editable after seeding.
 */
export const DEFAULT_INQUIRY_TYPES: readonly InquiryTypeSeed[] = [
  {
    id: 'student_support',
    name: 'Student support',
    description:
      'an enrolled student needs help with course content, an assignment, ' +
      'the IDE/tooling, or their account',
    typicalPriority: 1,
    risk: 'low',
    // Example self-serve body (auto-reply stays off until an admin opts in).
    autoReplyBody:
      'Thanks for reaching out to Code in Place! Most course, assignment, and ' +
      'IDE questions are answered on the help site and the course forum — ' +
      'please start there, and your section leader can help with anything ' +
      'specific to your section. A team member will follow up if your message ' +
      'needs a direct reply.',
    active: true,
    builtIn: false,
  },
  {
    id: 'application',
    name: 'Application',
    description:
      'about applying or getting in — application questions, deadlines, ' +
      'admission decisions, or enrollment status (the sender is not yet an ' +
      'enrolled student)',
    typicalPriority: 2,
    risk: 'low',
    // Example self-serve body (auto-reply stays off until an admin opts in).
    autoReplyBody:
      'Thanks for your interest in Code in Place! Application details, ' +
      'deadlines, and admission timelines are all on the Code in Place ' +
      "website — please check there first. We'll be in touch directly if your " +
      'question needs a personal response.',
    active: true,
    builtIn: false,
  },
  {
    id: 'sections_scheduling',
    name: 'Sections & scheduling',
    description:
      'a specific section: its meeting time, attendance, or a request to ' +
      'join/switch/drop a section',
    typicalPriority: 2,
    risk: 'low',
    active: true,
    builtIn: false,
  },
  {
    id: 'volunteer_teaching',
    name: 'Volunteer teaching',
    description:
      'someone offering to help teach — section-leader, mentor, or TA ' +
      'applications and their questions (not an enrolled student)',
    typicalPriority: 2,
    risk: 'low',
    active: true,
    builtIn: false,
  },
  {
    id: 'partnerships_press',
    name: 'Partnerships & press',
    description:
      'an outside party — another organization, a journalist/press, or a ' +
      'potential collaborator (not a student, applicant, or volunteer)',
    typicalPriority: 1,
    risk: 'medium',
    active: true,
    builtIn: false,
  },
  {
    id: 'donations_funding',
    name: 'Donations & funding',
    description:
      'money in or out — donations, sponsorship offers, grants, or vendor ' +
      'invoices',
    typicalPriority: 1,
    risk: 'high',
    active: true,
    builtIn: false,
  },
  {
    id: 'feedback',
    name: 'Feedback',
    description:
      'unsolicited thanks, praise, testimonials, or general feedback that ' +
      'needs no follow-up action',
    typicalPriority: 3,
    risk: 'low',
    active: true,
    builtIn: false,
  },
  {
    id: 'spam',
    name: 'Spam',
    description: 'bulk marketing, phishing, or clearly irrelevant mail',
    typicalPriority: 3,
    risk: 'low',
    active: true,
    builtIn: true,
  },
  {
    id: 'other',
    name: 'Other',
    description:
      'a genuine message that fits none of the categories above; routes to a ' +
      'human',
    typicalPriority: 2,
    risk: 'medium',
    active: true,
    builtIn: true,
  },
];

/**
 * The fallback slug the classifier normalizes an unknown category to. Always a
 * reserved built-in, so it is guaranteed to resolve.
 */
export const FALLBACK_INQUIRY_TYPE_ID = 'other' as const;

// ============================================================================
// Paths
// ============================================================================

/**
 * Inquiry-types collection path, nested under the reserved `_agent` bucket.
 *
 * @returns Firestore collection path `reports/_agent/inquiryTypes`.
 * @throws Never — pure string construction.
 */
export function getInquiryTypesCollectionPath(): string {
  return `${getBucketDocPath({bucketId: AGENT_BUCKET_ID})}/${AGENT_INQUIRY_TYPES_SUBCOLLECTION}`;
}

/**
 * Single inquiry-type document path.
 *
 * @param args.typeId - The inquiry-type slug (used as the document id).
 * @returns Firestore doc path `reports/_agent/inquiryTypes/{typeId}`.
 * @throws Never — pure string construction.
 */
export function getInquiryTypeDocumentPath({typeId}: {typeId: string}): string {
  return `${getInquiryTypesCollectionPath()}/${typeId}`;
}
